import { gql } from '@apollo/client'

import type {
  GQLStringOwner,
  NineField,
  NineFieldValue,
  NineInlineFragment,
  NineMutation,
  NineNodeBase,
  NineOperation,
  NineQuery,
  NineSubscription,
  NineUIProps,
  NineMetaProps,
  VarNode,
} from './types'
import { INDENT_STR, equalsProp, objToString } from './ngqlUtils'

// ============================================================================
// AST 빌더 (GraphQL 트리 노드 생성)
// ============================================================================

export function Fields(
  propField: Record<string, NineFieldValue>,
  strField?: string,
): Record<string, NineFieldValue> {
  if (strField) {
    const items = strField.split(/\n/)
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
        .replace(/(\s)*#[^\n]*$/, '')
        .trim()
        .split(/(\s)+/)
      for (let j = 0; j < item.length; j++) {
        if (item[j].length > 0) {
          propField[item[j]] = true
        }
      }
    }
  }
  return propField
}

export function InlineFragment(
  fields: Record<string, NineFieldValue>,
  ownerType: string,
): NineInlineFragment {
  return {
    nineType: 'inline-fragment',
    ownerType,
    fields,
  }
}

export function renamed(name: string, node: NineNodeBase): NineNodeBase {
  const renamedObj: NineNodeBase = {
    type: node.type,
    nineType: node.nineType,
    filters: { ...node.filters },
    fields: { ...node.fields },
    fragmentName: node.fragmentName,
    isArray: node.isArray === true,
    isNodeConnection: node.isNodeConnection === true,
    name,
  }

  if (node.extendUI != null) {
    renamedObj.extendUI = node.extendUI
  }

  return renamedObj
}

export function Field(
  enabled: boolean,
  type: string,
  uiProps?: NineUIProps,
  metaProps?: NineMetaProps,
): NineField {
  return {
    enabled,
    nineType: 'field',
    type,
    uiProps,
    metaProps,
  }
}

export function extendField(
  field: NineField,
  uiProps?: NineUIProps,
  metaProps?: NineMetaProps,
): NineField {
  const extField: NineField = {
    enabled: field.enabled,
    nineType: field.nineType,
    type: field.type,
    uiProps: { ...field.uiProps, ...uiProps },
    metaProps: { ...field.metaProps, ...metaProps },
  }

  return extField
}

// isFragment : true 인 경우에만 name을 사용.
export function Node(
  fields: Record<string, NineFieldValue>,
  type?: string | null,
  isFragment?: boolean,
  filters?: Record<string, VarNode | string | Record<string, unknown>>,
  isArray?: boolean,
  fragmentName?: string,
): NineNodeBase {
  const fragmentN = fragmentName || type
  const ret: NineNodeBase = {
    nineType: isFragment === true ? 'fragment' : 'node',
    type: type as string,
    fields,
    filters,
    fragmentName: fragmentN,
    isArray: isArray === true,
  }

  ret.extendUI = (uiProps: NineUIProps) => {
    ret.uiProps = uiProps
    return ret
  }

  return ret
}

export function NodeExt(
  parent: NineNodeBase,
  fields: Record<string, NineFieldValue>,
  fragmentName?: string,
): NineNodeBase {
  const fragmentN = fragmentName || parent.fragmentName
  return {
    nineType: parent.nineType,
    type: parent.type,
    fields: { ...parent.fields, ...fields },
    filters: parent.filters,
    fragmentName: fragmentN,
    isArray: parent.isArray,
  }
}

export function Query(name: string, fields: Record<string, NineFieldValue>): NineQuery {
  return {
    name,
    nineType: 'query',
    fields,
  }
}

export function Mutation(name: string, fields: Record<string, NineFieldValue>): NineMutation {
  return {
    name,
    nineType: 'mutation',
    fields,
  }
}

export function Subscription(
  name: string,
  fields: Record<string, NineFieldValue>,
): NineSubscription {
  return {
    name,
    nineType: 'subscription',
    fields,
  }
}

const PageInfo: NineNodeBase = Node(
  {
    hasNextPage: true,
    hasPreviousPage: true,
    startCursor: true,
    endCursor: true,
  },
  'PageInfo',
  true,
)

export function Edge(nodeType: string | null, node: NineNodeBase, nodeName?: string): NineNodeBase {
  const type = nodeType ? `${nodeType}Edge` : ''
  const isFragment = nodeType != null
  const nodeN = nodeName || 'node'
  const fragmentN = type

  const fields: Record<string, NineFieldValue> = {
    cursor: true,
  }
  fields[nodeN] = node

  return renamed(
    'edges',
    Node(fields, type, isFragment, null as unknown as undefined, true, fragmentN),
  )
}

export function NodeConnection(
  node: NineNodeBase | null,
  filters?: Record<string, VarNode | string | Record<string, unknown>>,
  isFragment?: boolean,
  fragmentName?: string,
  edgeName?: string,
  nodeName?: string,
): NineNodeBase {
  const edgeN = edgeName || 'edges'
  const type = node != null && node.type != null ? `${node.type}Connection` : ''

  const fragmentN = fragmentName || type

  const ret: NineNodeBase = {
    type,
    nineType: isFragment === true ? 'fragment' : 'node',
    filters,
    fields: {
      totalCount: true,
      edgeCount: true,
    } as Record<string, NineFieldValue>,
    fragmentName: fragmentN,
    isNodeConnection: true,
  }

  ret.extendUI = (uiProps: NineUIProps) => {
    ret.uiProps = uiProps
    return ret
  }

  if (node != null) {
    ret.fields!.pageInfo = PageInfo
    ret.fields![edgeN] = Edge(node.type, node, nodeName)
  }
  return ret
}

// totalCount, edgeCount가 없는 NodeConnection에 사용.
export function NodeConnection2(
  node: NineNodeBase | null,
  filters?: Record<string, VarNode | string | Record<string, unknown>>,
  isFragment?: boolean,
  fragmentName?: string,
  edgeName?: string,
  nodeName?: string,
): NineNodeBase {
  const edgeN = edgeName || 'edges'
  const type = node ? `${node.type}Connection` : ''

  const fragmentN = fragmentName || type

  const ret: NineNodeBase = {
    type,
    nineType: isFragment === true ? 'fragment' : 'node',
    filters,
    fields: {} as Record<string, NineFieldValue>,
    fragmentName: fragmentN,
    isNodeConnection: true,
  }

  if (node != null) {
    ret.fields!.pageInfo = PageInfo
    ret.fields![edgeN] = Edge(node.type, node, nodeName)
  }
  return ret
}

export function QueryItem(node: NineNodeBase, queryName?: string): NineNodeBase {
  return {
    type: node.type,
    nineType: node.nineType,
    filters: { ...node.filters },
    fields: { ...node.fields },
    fragmentName: node.fragmentName,
    isArray: node.isArray,
    name: node.name,
    queryName,
  }
}

export function Var(name: string, type: string): VarNode {
  return {
    nineType: 'var',
    name,
    type: type || 'String',
  }
}

// ============================================================================
// GraphQL 문자열 직렬화
// ============================================================================

export function getGQLString(
  nodeName: string | null,
  obj: NineNodeBase,
  parentIndent: string,
  owner: GQLStringOwner,
): string {
  let str = ''
  let fieldStr = ''
  let indent = parentIndent

  if (obj.filters) {
    Object.entries(obj.filters).forEach(([key, value0]) => {
      // value can be VarNode, string, or Record at runtime
      const value = value0 as VarNode | string | Record<string, unknown>
      if (typeof value !== 'string' && (value as VarNode).nineType === 'var') {
        if (owner.variableMap[key] == null) {
          owner.variableMap[key] = value as VarNode
        } else {
          console.warn('variable duplicated - ', key)
        }
      }
      const vNode = value as unknown as NineNodeBase
      const valueStr = vNode.nineType === 'var' ? `$${key}` : value
      const varKey = vNode.name != null ? vNode.name : key
      fieldStr += fieldStr.length > 0 ? ', ' : ''
      if (vNode.type != null && vNode.type.toLowerCase().indexOf('string') < 0) {
        fieldStr += `${varKey}: ${valueStr}`
      } else if (typeof value !== 'string') {
        if (vNode.nineType === 'var') {
          fieldStr += `${varKey}: ${valueStr}`
        } else {
          const isSORT = key === 'sort'
          const objStr = objToString(valueStr as Record<string, unknown>, isSORT)
          fieldStr += `${varKey}: ${objStr}`
        }
      } else {
        let fValue: string = `"${valueStr}"`
        if (key === 'sort') {
          fValue = valueStr as string
        }
        fieldStr += `${varKey}: ${fValue}`
      }
    })
    if (fieldStr.length > 0) {
      fieldStr = `(${fieldStr})`
    }
  }
  if (nodeName) {
    if (obj.nineType === 'inline-fragment') {
      // inline fragment 에는 아무 이름이나 넣어놓으면 된다. ( query가 생성될 때는 이름은 무시됨. )
      str += `${parentIndent}... on ${(obj as NineNodeBase & NineInlineFragment).ownerType} {\n`
    } else if (obj.queryName != null && nodeName !== obj.queryName) {
      str += `${parentIndent + nodeName}: ${obj.queryName + fieldStr} {\n`
    } else if (obj.name != null && nodeName !== obj.name) {
      str += `${parentIndent + nodeName}: ${obj.name + fieldStr} {\n`
    } else {
      str += `${parentIndent + nodeName + fieldStr} {\n`
    }
    indent = parentIndent + INDENT_STR
  }

  if (obj.nineType === 'fragment' && nodeName) {
    let fragmentName = obj.fragmentName || obj.type

    if (owner.fragmentMap[fragmentName] != null) {
      if (equalsProp(obj, owner.fragmentMap[fragmentName]) === false) {
        if (owner.fragmentNameTable[fragmentName] == null) {
          owner.fragmentNameTable[fragmentName] = 0
        }
        owner.fragmentNameTable[fragmentName] += 1

        fragmentName += owner.fragmentNameTable[fragmentName]
        obj.fragmentName = fragmentName
      }
    }

    if (owner.fragmentMap[fragmentName] == null) {
      owner.fragments.push(obj)
      owner.fragmentMap[fragmentName] = obj
    }

    str += `${indent}...${fragmentName}\n`
  } else {
    Object.entries(obj.fields!).forEach(([key, value0]) => {
      const value = value0 as NineFieldValue
      if (value != null) {
        if ((value as NineNodeBase).nineType === 'field') {
          if ((value as NineField).enabled !== false) {
            if ((value as NineField).type == null) {
              str += `${indent + key}\n`
            } else if (typeof (value as NineField).type === 'string') {
              str += `${indent + key}: ${(value as NineField).type}\n`
            } else {
              str += getGQLString(
                key,
                (value as NineField).type as unknown as NineNodeBase,
                indent,
                owner,
              )
            }
          }
        } else if (value !== false) {
          if (value === true) {
            str += `${indent + key}\n`
          } else if (typeof value === 'string') {
            str += `${indent + key}: ${value}\n`
          } else {
            str += getGQLString(key, value as NineNodeBase, indent, owner)
          }
        }
      }
    })
  }
  if (nodeName) {
    str += `${parentIndent}}\n`
  }
  return str
}

export function generateGQLString(obj: NineOperation): string {
  if (obj == null) {
    throw new Error('GQL must not be null.')
  }

  let str = ''
  const indent = INDENT_STR
  if (obj.nineType === 'query' || obj.nineType === 'mutation' || obj.nineType === 'subscription') {
    const owner: GQLStringOwner = {
      fragments: [],
      fragmentMap: {},
      variableMap: {},
      fragmentNameTable: {},
    }

    str += getGQLString(null, obj as unknown as NineNodeBase, indent, owner)

    str += `}\n`

    let idx = 0
    while (idx < owner.fragments.length) {
      const elem = owner.fragments[idx]
      str += `\n`
      str += `fragment ${elem.fragmentName || elem.type} on ${elem.type} {\n`
      str += getGQLString(null, elem, indent, owner)
      str += `}\n`
      idx += 1
    }

    let varStr = ''
    Object.entries(owner.variableMap).forEach(([key, varObj]) => {
      varStr += varStr.length > 0 ? ', ' : ''
      varStr += `$${key}: ${varObj.type}`
    })
    if (varStr.length > 0) {
      varStr = `(${varStr})`
    }

    const operationName = obj.name ? `${obj.nineType} ${obj.name + varStr}` : null

    str = str.replace(/{\s*}/g, '')

    if (operationName != null) {
      str = `${operationName} {\n${str}`
    } else {
      str = `${obj.nineType} {\n${str}`
    }
  }
  return str
}

export function generateGQL(obj: NineOperation): ReturnType<typeof gql> {
  const str = generateGQLString(obj)
  return gql(str)
}

// ============================================================================
// 응답 노드 데이터 추출 (arrayIFace 활용)
// ============================================================================

interface ArrayIFace {
  getNodes: (res: Record<string, unknown>) => unknown[]
  getTotalCount?: (res: Record<string, unknown>) => number
  getMoreCursor?: (res: Record<string, unknown>, last: unknown) => unknown
  [key: string]: unknown
}

export function getNodeData(
  res: Record<string, unknown>,
  key: string,
  arrayIFace: ArrayIFace | null | undefined,
): Record<string, unknown> | null {
  const resData = res[key] as Record<string, unknown> | undefined
  if (resData != null && typeof resData === 'object') {
    let data: Record<string, unknown> | null = null
    // type 비교를 위해 edgeCount와 totalCount는 query에 함께 포함되도록 할 것.
    if (arrayIFace != null) {
      data = {
        name: key,
        nodes: arrayIFace.getNodes(resData),
        totalCount: 0,
        isEdges: false,
        type: 'iface',
      }

      if (arrayIFace.getTotalCount != null) {
        data.totalCount = arrayIFace.getTotalCount(resData)
      }

      if (arrayIFace.getMoreCursor) {
        data.hasNext = arrayIFace.getMoreCursor(resData, null) != null
      }
    } else if (resData.edges != null || (resData.edgeCount != null && resData.totalCount != null)) {
      data = {
        name: key,
        nodes: resData.edges,
        totalCount: resData.totalCount,
        isEdges: true,
        type: 'edges',
      }

      if (resData.pageInfo) {
        data.hasNext = (resData.pageInfo as { hasNextPage?: boolean }).hasNextPage
      }
    } else if (Array.isArray(resData) === true) {
      data = {
        name: key,
        nodes: (resData as unknown as unknown[]).map((item) => {
          return { node: item }
        }),
        totalCount: (resData as unknown as unknown[]).length,
        isEdges: false,
        type: 'array',
      }
    } else {
      data = {
        name: key,
        nodes: [{ node: resData }],
        totalCount: 1,
        isEdges: false,
        type: 'node',
      }
    }

    return data
  }
  return null
}
